import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type CreatePartnerParams = {
  partnerName: string;
  partnerNameKana: string;
  partnerTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contactName?: string;
  contractStartDate: string;
  remarks?: string;
};

export type UpdatePartnerParams = {
  partnerId: string;
  partnerName: string;
  partnerNameKana: string;
  partnerTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contactName?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
};

export type GetPartnerParams = {
  partnerId: string;
};

export type ListPartnerParams = {
  partnerName?: string;
  partnerNameKana?: string;
  partnerTypeId?: string;
  includeExpired?: boolean;
};

export type PartnerResult = {
  partnerId: string;
  partnerName: string;
  partnerNameKana: string;
  partnerTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contactName?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

// --- API関数 ---

/**
 * パートナー登録API
 * @param params.partnerName - パートナー名（必須, 最大300文字）
 * @param params.partnerNameKana - パートナー名カナ（必須, 最大300文字）
 * @param params.partnerTypeId - パートナー区分ID（必須, 最大300文字）
 * @param params.corporateNumber - 法人番号（任意, 最大20文字）
 * @param params.invoiceNumber - 登録番号/インボイス番号 T+13桁（任意, 最大20文字）
 * @param params.address - 住所（任意, 最大500文字）
 * @param params.phoneNumber - 電話番号（任意, 最大20文字）
 * @param params.mailAddress - メールアドレス（任意, 最大300文字）
 * @param params.contactName - 担当者名（任意, 最大300文字）
 * @param params.contractStartDate - 契約開始日（必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const createPartner = async (params: CreatePartnerParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_PARTNER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.partnerId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * パートナー更新API
 * @param params.partnerId - パートナーID（必須, 最大64文字）
 * @param params.partnerName - パートナー名（必須, 最大300文字）
 * @param params.partnerNameKana - パートナー名カナ（必須, 最大300文字）
 * @param params.partnerTypeId - パートナー区分ID（必須, 最大300文字）
 * @param params.corporateNumber - 法人番号（任意, 最大20文字）
 * @param params.invoiceNumber - 登録番号/インボイス番号 T+13桁（任意, 最大20文字）
 * @param params.address - 住所（任意, 最大500文字）
 * @param params.phoneNumber - 電話番号（任意, 最大20文字）
 * @param params.mailAddress - メールアドレス（任意, 最大300文字）
 * @param params.contactName - 担当者名（任意, 最大300文字）
 * @param params.contractStartDate - 契約開始日（必須, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.contractEndDate - 契約終了日（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.remarks - 備考（任意, 最大10000文字）
 */
export const updatePartner = async (params: UpdatePartnerParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_PARTNER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * パートナー取得API
 * @param params.partnerId - パートナーID（必須, 最大64文字）
 */
export const getPartner = async (params: GetPartnerParams): Promise<PartnerResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_PARTNER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * パートナー一覧取得API
 * @param params.partnerName - パートナー名（任意, 最大300文字, 前方一致）
 * @param params.partnerNameKana - パートナー名カナ（任意, 最大300文字, 前方一致）
 * @param params.partnerTypeId - パートナー区分ID（任意, 最大300文字, 前方一致）
 * @param params.includeExpired - 契約終了を含む（任意, boolean）
 */
export const listPartner = async (params: ListPartnerParams): Promise<PartnerResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_PARTNER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
